import React, { useState, useEffect, useRef } from 'react';
import { styled } from 'linaria/react';
import Slider from 'react-slick';
import Image from '@jetshop/ui/Image/';
import Play from '../../../assets/BW-Play.png';
const ThumbSliderWrapper = styled('div')`
  margin-top: 12px;
  scroll-margin-top: 0;

  /* Prevent scroll behavior when focusing */
  scroll-behavior: auto !important;

  .slick-slide {
    max-width: 160px;
    > div {
      padding: 4px;
    }

    /* Prevent focus from causing scroll */
    &:focus {
      outline: none;
    }

    * {
      /* Prevent any child elements from being focusable initially */
      &:focus {
        outline: none;
      }
    }
  }

  .thumb {
    padding: 4px;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #f4f4f4;
    overflow: hidden;
  }

  .slick-current .thumb {
    outline: 1px solid rgba(55, 55, 55, 0.5);
  }

  .thumb .thumb-360 {
    height: 100%;
    width: 100%;
    padding: 6px 8px;
    border-radius: 8px;
    background: #f2f2f2;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    margin: auto;
  }

  .video-thumb {
    position: relative;
    overflow: hidden;
  }

  .play-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: auto;
    height: 50%;
  }

  .video-thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
    scale: 1.3;
  }
`;

export const ThumbSlider = ({
  media,
  aspect,
  thumbsRef,
  navMain,
  videoThumbnail,
  currentSlideIndex,
}) => {
  const type360 = '360';
  const [key, setKey] = useState(0);
  const [isClient, setIsClient] = useState(false);
  const resizeTimeoutRef = useRef(null);
  const prevNavMainRef = useRef(navMain);
  const [focusOnSelect, setFocusOnSelect] = useState(false);

  useEffect(() => {
    setIsClient(true);

    const handleResize = () => {
      // Clear any pending resize timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      // Debounce resize to avoid too many re-renders
      resizeTimeoutRef.current = setTimeout(() => {
        setKey((prev) => prev + 1); // Re-mount to reinitialize slider
        setFocusOnSelect(false); // Reset on remount
      }, 300);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  // Reinitialize when navMain changes (but not on initial mount)
  useEffect(() => {
    if (navMain && prevNavMainRef.current !== navMain && thumbsRef.current) {
      setKey((prev) => prev + 1);
      setFocusOnSelect(false); // Reset on remount
    }
    prevNavMainRef.current = navMain;
  }, [navMain]);

  // Sync to current slide after remount or when currentSlideIndex changes
  useEffect(() => {
    if (thumbsRef.current && currentSlideIndex !== undefined) {
      const timer = setTimeout(() => {
        if (thumbsRef.current?.slickGoTo) {
          // Store current scroll position
          const scrollY = window.scrollY;
          const scrollX = window.scrollX;

          // Temporarily prevent scroll restoration
          if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
          }

          thumbsRef.current.slickGoTo(currentSlideIndex, false);

          // Restore scroll position after a brief delay
          requestAnimationFrame(() => {
            window.scrollTo(scrollX, scrollY);
            if ('scrollRestoration' in window.history) {
              window.history.scrollRestoration = 'auto';
            }
          });
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [key, currentSlideIndex]);

  // Prevent focus on initial mount
  useEffect(() => {
    if (thumbsRef.current && isClient) {
      // Remove focus from any focused elements in the slider
      const sliderElement = thumbsRef.current.innerSlider?.list;
      if (sliderElement) {
        const focusedElement = sliderElement.querySelector(':focus');
        if (focusedElement) {
          focusedElement.blur();
        }
        // Also prevent any child elements from being focusable initially
        const allFocusable = sliderElement.querySelectorAll(
          'a, button, [tabindex]'
        );
        allFocusable.forEach((el) => {
          if (el.tabIndex >= 0) {
            el.setAttribute('data-original-tabindex', el.tabIndex);
            el.tabIndex = -1;
          }
        });
      }
    }
  }, [isClient, key]);

  // Enable focusOnSelect after slider is mounted to prevent initial scroll
  useEffect(() => {
    if (thumbsRef.current && isClient) {
      // Wait for slider to fully initialize, then enable focusOnSelect
      const timer = setTimeout(() => {
        setFocusOnSelect(true);
        // Restore tabindex after delay
        const sliderElement = thumbsRef.current.innerSlider?.list;
        if (sliderElement) {
          const allFocusable = sliderElement.querySelectorAll(
            '[data-original-tabindex]'
          );
          allFocusable.forEach((el) => {
            const originalTabIndex = el.getAttribute('data-original-tabindex');
            if (originalTabIndex !== null) {
              el.tabIndex = parseInt(originalTabIndex, 10);
              el.removeAttribute('data-original-tabindex');
            }
          });
        }
      }, 900);
      return () => clearTimeout(timer);
    }
  }, [isClient, key]);

  const baseThumbSettings = {
    slidesToShow: Math.min(media?.length || 10, 11),
    slidesToScroll: 1,
    swipeToSlide: true,
    focusOnSelect: focusOnSelect,
    arrows: false,
    dots: false,
    centerMode: false,
    infinite: false,
    asNavFor: navMain,
    accessibility: false, // Disable keyboard navigation to prevent focus issues
  };
  const thumbsSettings = {
    ...baseThumbSettings,
    responsive: [
      {
        breakpoint: 1600,
        settings: { slidesToShow: 8 },
        // media?.length < 8
        //   ? 'unslick'
        //   : {
        //       slidesToShow: 8,
        //     },
      },
      {
        breakpoint: 1200,
        settings: { slidesToShow: 6 },
        // media?.length < 6
        //   ? 'unslick'
        //   : {
        //       slidesToShow: 6,
        //     },
      },
      {
        breakpoint: 900,
        settings: { slidesToShow: 4 },
        // media?.length < 4
        //   ? 'unslick'
        //   : {
        //       slidesToShow: 4,
        //     },
      },
    ],
  };

  if (!isClient)
    return (
      <ThumbSliderWrapper className="thumbs-slider-wrapper" key={key}>
        {media?.map((m, index) =>
          m?.type === type360 ? (
            <div key={`thumb-360-${index}`} className="thumb">
              {/* Keep it simple: a badge or icon for 360 */}
              <div className="thumb-360">360°</div>
            </div>
          ) : (
            <>
              {m.type === 'video' ? (
                <div key={`thumb-${m.url || index}`} className="thumb">
                  <img src={videoThumbnail} alt={m?.alt || ''} />
                </div>
              ) : (
                <div key={`thumb-${m.url || index}`} className="thumb">
                  <Image
                    src={m?.url}
                    alt={m?.alt || ''}
                    sizes={'150'}
                    aspect={aspect}
                    fillAvailableSpace={true}
                  />
                </div>
              )}
            </>
          )
        )}
      </ThumbSliderWrapper>
    );

  return (
    <ThumbSliderWrapper
      className="thumbs-slider-wrapper"
      key={`${key}-${navMain ? 'nav' : 'no-nav'}`}
    >
      <Slider {...thumbsSettings} ref={thumbsRef}>
        {media?.map((m, index) =>
          m?.type === type360 ? (
            <div key={`thumb-360-${index}`} className="thumb">
              {/* Keep it simple: a badge or icon for 360 */}
              <div className="thumb-360">360°</div>
            </div>
          ) : (
            <>
              {m.type === 'video' ? (
                <div
                  key={`thumb-${m.url || index}`}
                  className="thumb video-thumb"
                >
                  <img
                    src={videoThumbnail}
                    alt={'video thumbnail'}
                    className="video-thumbnail"
                  />
                  <img src={Play} alt={'play'} className="play-icon" />
                </div>
              ) : (
                <div key={`thumb-${m.url || index}`} className="thumb">
                  <Image
                    src={m?.url}
                    alt={m?.alt || ''}
                    sizes={'150'}
                    aspect={aspect}
                    fillAvailableSpace={true}
                  />
                </div>
              )}
            </>
          )
        )}
      </Slider>
    </ThumbSliderWrapper>
  );
};
